import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";
import type { BookingStatus } from "@/lib/status-colors";

/*
 * Badge — DESIGN_SYSTEM.md §2.1
 *
 * Two complementary APIs:
 *   - `variant` — generic visual variant: default | secondary | destructive
 *                | outline. Backed by shadcn primitive aliases.
 *   - `tone`    — semantic tone: neutral | info | success | warn | danger.
 *                 When set, takes precedence over `variant`. Use this for
 *                 status/state pills (booking status, account state, etc.).
 *
 * Status helpers (`pending`, `confirmed`, `cancelled`, `completed`,
 * `no_show`) alias to a tone; pair with `{statusBadgeColors[status]}` from
 * `lib/status-colors` when you also need the soft background + border.
 *
 * The standalone StatusBadge component is a thin wrapper for back-compat;
 * new code should prefer `<Badge tone="…">label</Badge>`.
 */

const badgeVariants = cva(
  "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 cursor-default",
  {
    variants: {
      variant: {
        default:
          "border-transparent bg-primary text-primary-foreground",
        secondary:
          "border-transparent bg-secondary text-secondary-foreground",
        destructive:
          "border-transparent bg-destructive text-destructive-foreground",
        outline: "text-foreground",
      },
      tone: {
        neutral: "border-transparent bg-surface-2 text-soft",
        info:    "border-transparent bg-brand-50 text-brand-700",
        success: "border-transparent bg-success-soft text-success-text",
        warn:    "border-transparent bg-warn-soft text-warn-text",
        danger:  "border-transparent bg-danger-soft text-danger-text",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

export type BadgeTone = NonNullable<VariantProps<typeof badgeVariants>['tone']>;

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {}

const Badge = React.forwardRef<HTMLDivElement, BadgeProps>(
  ({ className, variant, tone, ...props }, ref) => {
    // When tone is set, ignore variant so the two systems don't fight.
    const resolved = tone ? badgeVariants({ tone, className }) : badgeVariants({ variant, className });
    return <div ref={ref} className={cn(resolved)} {...props} />;
  }
);
Badge.displayName = 'Badge';

/* ----------------------------------------------------------------------
 * StatusBadge — booking-status convenience wrapper.
 * Maps a BookingStatus enum to the right semantic tone. Thin pass-through.
 * -------------------------------------------------------------------- */

const STATUS_TO_TONE: Record<BookingStatus, BadgeTone> = {
  pending:   'warn',
  confirmed: 'success',
  cancelled: 'danger',
  completed: 'info',
  no_show:   'neutral',
};

const STATUS_BADGE_SIZE: Record<'sm' | 'md', string> = {
  sm: 'px-2 py-0.5 text-xs',
  md: 'px-2.5 py-0.5 text-xs',
};

export interface StatusBadgeProps extends React.HTMLAttributes<HTMLDivElement> {
  status: BookingStatus;
  size?: 'sm' | 'md';
}

const StatusBadge = React.forwardRef<HTMLDivElement, StatusBadgeProps>(
  ({ status, size = 'md', className, children, ...props }, ref) => {
    const content = children ?? status.replace('_', ' ');
    return (
      <Badge
        ref={ref}
        tone={STATUS_TO_TONE[status]}
        className={cn(STATUS_BADGE_SIZE[size], className)}
        {...props}
      >
        {content}
      </Badge>
    );
  }
);
StatusBadge.displayName = 'StatusBadge';

export { Badge, StatusBadge, badgeVariants };
